import { getJSON } from 'common/services/api'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useMemo } from 'react'
import { useQueries, useQuery } from 'react-query'
import { getQueryParams } from 'utils/getQueryParams'
import type { RotaEventInstance } from '../types'
import { YEAR_FIRST_DATE_FORMAT } from '../utils.datetime'
import { REVISIONS_QUERY_KEY, REVISIONS_URL } from './queryConstants'

dayjs.extend(isoWeek)

const PAGE_LIMIT = 1000
const FIELDS = [
  '*',
  'carer.picture',
  'resident.name',
  'resident.picture',
  'resident.status'
]

type UseRecursiveRevisionsListReturn = {
  revisions: RotaEventInstance[]
  isLoading: boolean
  errorCode: number
}

type UseRecursionRevisionsListProps = {
  selectedDate?: Date
  intervalType?: 'daily' | 'weekly'
  otherFilterParams?: Record<string, any>
}

const useRecursionRevisionsList = ({
  selectedDate,
  intervalType,
  otherFilterParams = {}
}: UseRecursionRevisionsListProps = {}): UseRecursiveRevisionsListReturn => {
  const hasAllocationFilter = !!otherFilterParams?.allocationStatuses?.length

  const date =
    intervalType === 'weekly'
      ? dayjs(selectedDate).startOf('isoWeek').format(YEAR_FIRST_DATE_FORMAT)
      : dayjs(selectedDate).format(YEAR_FIRST_DATE_FORMAT)

  const baseParams = getQueryParams({
    date,
    intervalType,
    fields: FIELDS,
    ...otherFilterParams
  })

  // Allocation filter active: single unbounded request (backend full-scans anyway)
  const { isLoading: singleIsLoading, data: singleData } = useQuery(
    [REVISIONS_QUERY_KEY, baseParams, 'single'],
    () =>
      getJSON({
        url: `${REVISIONS_URL}?${baseParams}`,
        isRota: true,
        customErrorHandling: true
      }),
    {
      enabled: hasAllocationFilter && !!selectedDate && !!intervalType,
      retry: false
    }
  )

  // No allocation filter: always paginate starting from page 1
  const page1Params = getQueryParams({
    date,
    intervalType,
    fields: FIELDS,
    ...otherFilterParams,
    page: 1,
    limit: PAGE_LIMIT
  })

  const { isLoading: page1IsLoading, data: page1Data } = useQuery(
    [REVISIONS_QUERY_KEY, page1Params],
    () =>
      getJSON({
        url: `${REVISIONS_URL}?${page1Params}`,
        isRota: true,
        customErrorHandling: true
      }),
    {
      enabled: !hasAllocationFilter && !!selectedDate && !!intervalType,
      retry: false
    }
  )

  const totalPages: number = page1Data?.pagination?.totalPages ?? 1

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  const remainingQueries = useMemo(
    () =>
      Array.from({ length: Math.max(0, totalPages - 1) }).map((_, i) => {
        const page = i + 2
        const pageParams = getQueryParams({
          date,
          intervalType,
          fields: FIELDS,
          ...otherFilterParams,
          page,
          limit: PAGE_LIMIT
        })
        return {
          queryKey: [REVISIONS_QUERY_KEY, pageParams],
          queryFn: () =>
            getJSON({
              url: `${REVISIONS_URL}?${pageParams}`,
              isRota: true,
              customErrorHandling: true
            }),
          enabled: !hasAllocationFilter && !!page1Data && totalPages > 1
        }
      }),
    [totalPages, page1Params, hasAllocationFilter, page1Data]
  )

  const remainingResults = useQueries(remainingQueries)

  const page1Events = page1Data?.data || []
  const remainingEvents = remainingResults.flatMap((r) => r?.data?.data || [])
  const paginatedRevisions = [...page1Events, ...remainingEvents]

  const finalRevisions = hasAllocationFilter
    ? singleData?.data || []
    : paginatedRevisions

  const isLoading = hasAllocationFilter
    ? singleIsLoading
    : page1IsLoading || remainingResults.some((r) => r.isLoading)

  const errorCode = hasAllocationFilter
    ? singleData?.statusCode
    : page1Data?.statusCode ||
      (
        remainingResults.find((r) => r.error)?.error as
          | { statusCode?: number }
          | undefined
      )?.statusCode

  return {
    revisions: finalRevisions,
    isLoading,
    errorCode
  }
}

export default useRecursionRevisionsList
